import React from 'react';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { alpha, Box, Stack } from '@mui/material';
import { prepareFormElementProps } from 'components/common/form/formTools';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import useBundleTranslation from 'i18n';

export interface IFormJsonTemplateWithPlaceholdersProps extends FormControlProps {
    mode: string;
    height: string;
    variablesList: { key: string; value: string; isHeader?: boolean }[];
}

export default function FormJsonTemplateWithPlaceholders({
    controlProps: props,
}: FormElementControlPropsType<IFormJsonTemplateWithPlaceholdersProps>) {
    const { mode, height, variablesList } = props;
    const elementProps = useCustomSimplifiedForm();
    const { t } = useBundleTranslation(['app/editor/user/access-request-profile']);

    return (
        <Box>
            <Stack direction="row" spacing={2}>
                <Box sx={{ flexGrow: 1 }}>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                name: props.name,
                                component: 'CodeEditor',
                                props: {
                                    height: height || '400px',
                                    mode: mode || 'json',
                                },
                            },
                            form: props.form,
                        })}
                    />
                </Box>
                <Box
                    sx={{
                        width: 264,
                        flexShrink: 0,
                        p: 2,
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                        borderRadius: 1,
                    }}
                >
                    <Box sx={{ color: 'primary.main', fontWeight: 600, pb: 1, lineHeight: 1.15 }}>
                        {t('available_placeholders')}
                    </Box>
                    {variablesList && variablesList.length > 0 && (
                        <Box sx={{ pb: 1 }}>
                            {variablesList.map((variable, index) =>
                                variable.isHeader ? (
                                    <Box
                                        key={`header-${index}`}
                                        sx={{
                                            pt: index > 0 ? 1.5 : 0,
                                            pb: 0.5,
                                            fontWeight: 600,
                                            fontSize: '0.85em',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {variable.value}
                                    </Box>
                                ) : (
                                    <Box key={variable.key} sx={{ py: 0.25, pl: 0.5 }}>
                                        <Box component="strong">{variable.key}</Box>
                                        {variable.value && (
                                            <Box component="span" sx={{ color: 'text.secondary' }}>
                                                {' '}
                                                - {variable.value}
                                            </Box>
                                        )}
                                    </Box>
                                ),
                            )}
                        </Box>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}

export class FormJsonTemplateWithPlaceholdersBuilder extends FormComponentBuilder {
    prepareProps(): IFormJsonTemplateWithPlaceholdersProps {
        return {
            ...this.controlProps,
            mode: this.elementProps.componentProps?.mode ?? 'json',
            height: this.elementProps.componentProps?.height ?? '400px',
            variablesList: this.elementProps.componentProps?.variablesList ?? [],
        };
    }
}
